import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { ChangeEvent, useState } from 'react';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import { isSupportedISP, isValidDomain } from '../../../../../models/utils/validationUtils';
import { useAutoDiscoverEmailMutation } from '../../../../../queries/Admin/Email/useAutoDiscoverEmailMutation';
import { useDefaultEmailConfigurationQuery } from '../../../../../queries/Admin/Email/useDefaultEmailConfigurationQuery';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { Dialog, DialogBody, DialogPopup, DialogTitle } from '../../../../../web/components-v2/Dialog';
import { Input } from '../../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../web/components-v2/Select';
import { DangerMessage } from '../../../../../web/components/errorMessages/DangerMessage';
import { EMAIL_PROVIDER_OPTIONS, IEmailConfigDraft, USE_LEV_DOMAIN_OPTIONS } from './emailConfigTypes';
import { emailConfigurationRowStyles as s } from './emailConfigurationRowStyles';

export interface IEmailConfigurationRowProps {
	configuration: IEmailConfigDraft;
	accountId: string;
	/** Legacy `haveSupportLevel3` gate for the "Use Levitate Domain" checkbox + lev-domain select. */
	canUseLevDomain: boolean;
	/** The account's provisioned dedicated send domain (`features.sendEmail.domainName`), offered
	 * alongside the shared Levitate domains so a customer-specific domain is selectable here. */
	configuredSendDomain?: string;
	onChange: (config: IEmailConfigDraft) => void;
	/** When undefined, the Delete button is hidden (non-deletable row). */
	onDelete?: () => void;
}

interface IImapProvider {
	configuration: Api.IImapEmailConfiguration;
	displayName: string;
}

/** ISP → host/port preset bundles, copied verbatim from the legacy EmailConfiguration row. */
const IMAP_PROVIDERS: IImapProvider[] = [
	{
		configuration: {
			imapHost: 'secure.emailsrvr.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'secure.emailsrvr.com',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Rackspace',
	},
	{
		configuration: {
			imapHost: 'imap.secureserver.net',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtpout.secureserver.net',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'GoDaddy',
	},
	{
		configuration: {
			imapHost: 'imap.1and1.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.1and1.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: '1and1',
	},
	{
		configuration: {
			imapHost: 'imap.fusemail.net',
			imapPort: 143,
			imapUseSsl: false,
			smtpHost: 'smtp.fusemail.net',
			smtpPort: 2500,
			smtpUseSsl: false,
		},
		displayName: 'FuseMail',
	},
	{
		configuration: {
			imapHost: 'imap.mail.yahoo.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.mail.yahoo.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: 'Yahoo',
	},
	{
		configuration: {
			imapHost: 'imap.aol.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.aol.com',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Aol',
	},
	{
		configuration: {
			imapHost: 'imap.mail.me.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.mail.me.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: 'iCloud',
	},
	{
		configuration: {
			imapHost: 'mail.smarshmail.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'mail.smarshmail.com',
			smtpPort: 587,
			smtpUseSsl: true,
		},
		displayName: 'Smarshmail',
	},
	{
		configuration: {
			imapHost: 'imap.redtailtechnology.com',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: 'smtp.redtailtechnology.com',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Redtail',
	},
	{
		configuration: {
			imapHost: '',
			imapPort: 993,
			imapUseSsl: true,
			smtpHost: '',
			smtpPort: 465,
			smtpUseSsl: true,
		},
		displayName: 'Other',
	},
];

const NETWORK_PROXY_OPTIONS: { value: Api.NetworkProxyEndpoint; label: string }[] = [
	{ value: Api.NetworkProxyEndpoint.None, label: 'None' },
	{ value: Api.NetworkProxyEndpoint.Azure, label: 'Azure' },
	{ value: Api.NetworkProxyEndpoint.DigitalOcean, label: 'DigitalOcean' },
	{ value: Api.NetworkProxyEndpoint.Linode, label: 'Linode' },
];

/** Reads the count for a given interval; `undefined` when absent from a present array, `null` when no array. */
function getEmailSendLimit(
	intervalInMinutes: number,
	emailSendLimits?: Api.IEmailSendLimit[]
): number | null | undefined {
	if (emailSendLimits) {
		const limit = emailSendLimits.find(x => x.intervalInMinutes === intervalInMinutes);
		return limit ? limit.count : undefined;
	}
	return null;
}

/** Upserts (or drops, for a negative count) the limit for `intervalInMinutes`, returning a new array. */
function setEmailSendLimit(
	emailSendLimits: Api.IEmailSendLimit[] | undefined,
	intervalInMinutes: number,
	countStr: string
): Api.IEmailSendLimit[] {
	const count = parseInt(countStr, 10);
	if (!emailSendLimits || emailSendLimits.length === 0) {
		return count >= 0 ? [{ count, intervalInMinutes }] : [];
	}
	const next = emailSendLimits.filter(x => x.intervalInMinutes !== intervalInMinutes);
	if (count >= 0) {
		next.push({ count, intervalInMinutes });
	}
	return next;
}

/** Legacy port/seconds parse: '0' → 0, otherwise the parsed int or null (empty/invalid clears the field). */
function parseIntFieldValue(value: string): number | null {
	return value === '0' ? 0 : parseInt(value, 10) || null;
}

function getIntervalText(intervalInMinutes: number): string {
	switch (intervalInMinutes) {
		case 1440:
			return 'day';
		case 60:
			return 'hour';
		default:
			return `${intervalInMinutes} minutes`;
	}
}

function getRoundedMegabytes(maxMessageSizeInBytes: number): number {
	const maxMessageSizeInMegaBytes = maxMessageSizeInBytes / 1000000;
	return Math.round((maxMessageSizeInMegaBytes + Number.EPSILON) * 100) / 100;
}

/**
 * MobX-free auto-discover flow — the replacement for `AutoDiscoverEmailModal` +
 * `AutoDiscoverEmailViewModel`. An email input + "Go" drives {@link useAutoDiscoverEmailMutation};
 * the discovered configuration is previewed and, on "Use This Configuration", handed to `onApply`.
 */
const AutoDiscoverDialog = ({
	initialDomain,
	onApply,
	onClose,
}: {
	initialDomain?: string;
	onApply: (config: Api.IEmailProviderConfiguration) => void;
	onClose: () => void;
}) => {
	const [email, setEmail] = useState(initialDomain ? `test@${initialDomain}` : '');
	const autoDiscover = useAutoDiscoverEmailMutation();
	const config = autoDiscover.data;
	const errorMessage = autoDiscover.isError
		? ((autoDiscover.error as unknown as Api.IOperationResultNoValue)?.systemMessage ??
			'Unable to auto discover configuration.')
		: null;

	return (
		<Dialog
			open
			onOpenChange={next => {
				if (!next) {
					onClose();
				}
			}}
			dismissOnOutsideClick={false}
		>
			<DialogPopup styles={[s.dialogPopup]}>
				<DialogTitle>Auto Discover Email Configuration</DialogTitle>
				<DialogBody styles={[s.dialogBody]}>
					<div className={css(s.inputRow)}>
						<Input
							value={email}
							onChange={e => setEmail(e.target.value)}
							placeholder='Email Address'
							disabled={autoDiscover.isPending}
							styles={[s.hostName]}
						/>
						<Button onClick={() => autoDiscover.mutate({ email })} isLoading={autoDiscover.isPending}>
							Go
						</Button>
					</div>
					{errorMessage && <div className={css(s.dialogError)}>{errorMessage}</div>}
					{config && (
						<div className={css(s.dialogResults)}>
							<div className={css(s.header)}>Email Provider</div>
							<div className={css(s.dialogValue)}>{config.emailProvider}</div>
							{!!config.imap?.imapHost && !!config.imap?.imapPort && (
								<>
									<div className={css(s.header)}>IMAP Configuration</div>
									<div className={css(s.dialogValue)}>{`${config.imap.imapHost} (${config.imap.imapPort})`}</div>
								</>
							)}
							{!!config.imap?.smtpHost && !!config.imap?.smtpPort && (
								<>
									<div className={css(s.header)}>SMTP Configuration</div>
									<div className={css(s.dialogValue)}>{`${config.imap.smtpHost} (${config.imap.smtpPort})`}</div>
								</>
							)}
							{!!config.exchange?.webServicesUrl && (
								<>
									<div className={css(s.header)}>Exchange Url</div>
									<div className={css(s.dialogValue)}>{config.exchange.webServicesUrl}</div>
								</>
							)}
							{!!config.resolvedConfiguration?.emailSendLimits?.length && (
								<>
									<div className={css(s.header)}>Send Limits</div>
									{config.resolvedConfiguration.emailSendLimits.map((sendLimit, index) => (
										<div key={index.toString()} className={css(s.dialogValue)}>
											{`${sendLimit.count} per ${getIntervalText(sendLimit.intervalInMinutes)}`}
										</div>
									))}
								</>
							)}
							{!!config.resolvedConfiguration?.secondsBetweenSends &&
								config.resolvedConfiguration.secondsBetweenSends > 0 && (
									<>
										<div className={css(s.header)}>Seconds Between Sends</div>
										<div className={css(s.dialogValue)}>{config.resolvedConfiguration.secondsBetweenSends}</div>
									</>
								)}
							{!!config.resolvedConfiguration?.maxMessageSizeInBytes &&
								config.resolvedConfiguration.maxMessageSizeInBytes > 0 && (
									<>
										<div className={css(s.header)}>Max Message Size</div>
										<div className={css(s.dialogValue)}>
											{`${getRoundedMegabytes(config.resolvedConfiguration.maxMessageSizeInBytes)} MB`}
										</div>
									</>
								)}
							<Button
								styles={[s.useConfigButton]}
								onClick={() => {
									onApply(config);
									onClose();
								}}
							>
								Use This Configuration
							</Button>
						</div>
					)}
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};

/**
 * A single email-provider configuration editor — the MobX-free rebuild of the legacy
 * `EmailConfiguration` observer row. Fully controlled: every field edit emits the updated draft via
 * `onChange`; only transient UI (auto-discover dialog, ISP preset selection, and the "Use Levitate
 * Domain" toggle) lives in local state.
 */
export const EmailConfigurationRow = ({
	accountId,
	configuration,
	canUseLevDomain,
	configuredSendDomain,
	onChange,
	onDelete,
}: IEmailConfigurationRowProps) => {
	const [imapISP, setImapISP] = useState('Other');
	const [useLevDomain, setUseLevDomain] = useState(
		() => !!configuration.sendFrom?.domain || !isSupportedISP(configuration.domain)
	);
	const [autoDiscoverOpen, setAutoDiscoverOpen] = useState(false);

	const { domain, emailProvider, secondsBetweenSends } = configuration;

	// The domain is typed a character at a time, so only ask the server about a settled value. The
	// hook seeds its state with the initial domain, so a row opened with a saved domain fetches at once.
	const debouncedDomain = useDebounceValue(domain ?? '');
	const resolvedConfiguration = useDefaultEmailConfigurationQuery({
		accountId,
		domain: debouncedDomain,
		enabled: isValidDomain(debouncedDomain),
		provider: emailProvider,
	}).data;

	const emit = (patch: Partial<IEmailConfigDraft>) => onChange({ ...configuration, ...patch });
	const emitImap = (patch: Partial<Api.IImapEmailConfiguration>) => emit({ imap: { ...configuration.imap, ...patch } });
	const emitExchange = (patch: Partial<Api.IExchangeEmailConfiguration>) =>
		emit({ exchange: { ...configuration.exchange, ...patch } });

	const dailyLimit = getEmailSendLimit(1440, configuration.emailSendLimits);
	const hourlyLimit = getEmailSendLimit(60, configuration.emailSendLimits);
	const defaultDailyLimit = getEmailSendLimit(1440, resolvedConfiguration?.emailSendLimits);
	const defaultHourlyLimit = getEmailSendLimit(60, resolvedConfiguration?.emailSendLimits);
	const defaultSecondsBetweenSends = resolvedConfiguration?.secondsBetweenSends;

	// Legacy candidate-domain logic: the 4 lev options, collapsing to just the account domain when a
	// support-L3 admin has a non-lev domain.
	let candidateDomains = USE_LEV_DOMAIN_OPTIONS;
	if (canUseLevDomain && domain && !candidateDomains.includes(domain)) {
		candidateDomains = [domain];
	}
	if (configuredSendDomain && !candidateDomains.includes(configuredSendDomain)) {
		candidateDomains = [configuredSendDomain, ...candidateDomains];
	}
	const selectedSendDomain = configuration.sendFrom?.domain;
	if (selectedSendDomain && !candidateDomains.includes(selectedSendDomain)) {
		candidateDomains = [selectedSendDomain, ...candidateDomains];
	}

	const handleDomainChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (!isSupportedISP(value)) {
			setUseLevDomain(true);
			emit({ domain: value, sendFrom: { domain: 'myagent.email' } });
		} else {
			emit({ domain: value });
		}
	};

	const handleProviderChange = (value: string) => {
		const provider = value as Api.EmailProviderType;
		let imap = configuration.imap;
		if (provider === 'Yahoo' || provider === 'Aol') {
			const preset = IMAP_PROVIDERS.find(p => p.displayName === provider);
			if (preset) {
				imap = { ...imap, ...preset.configuration };
			}
		}
		emit({ emailProvider: provider, emailSendLimits: setEmailSendLimit([], 0, '0'), imap });
	};

	const handleUseLevDomainChange = (checked: boolean) => {
		emit({ sendFrom: null });
		setUseLevDomain(checked);
	};

	const handleLevDomainChange = (value: string) => emit({ sendFrom: { domain: value } });

	const handleDailyLimitChange = (e: ChangeEvent<HTMLInputElement>) =>
		emit({ emailSendLimits: setEmailSendLimit(configuration.emailSendLimits, 1440, e.target.value) });

	const handleHourlyLimitChange = (e: ChangeEvent<HTMLInputElement>) =>
		emit({ emailSendLimits: setEmailSendLimit(configuration.emailSendLimits, 60, e.target.value) });

	const handleBetweenSendsChange = (e: ChangeEvent<HTMLInputElement>) =>
		emit({ secondsBetweenSends: parseIntFieldValue(e.target.value) });

	const handleExchangeUrlChange = (e: ChangeEvent<HTMLInputElement>) =>
		emitExchange({ webServicesUrl: e.target.value });

	const handleImapISPChange = (value: string) => {
		setImapISP(value);
		const preset = IMAP_PROVIDERS.find(p => p.displayName === value);
		if (preset) {
			emitImap({ ...preset.configuration });
		}
	};

	const handleProxyChange = (value: string) => {
		const endpoint = value as Api.NetworkProxyEndpoint;
		const proxy: Api.INetworkProxy =
			endpoint === Api.NetworkProxyEndpoint.Azure ||
			endpoint === Api.NetworkProxyEndpoint.DigitalOcean ||
			endpoint === Api.NetworkProxyEndpoint.Linode
				? { endpoint, type: Api.NetworkProxyType.Socks5 }
				: { endpoint: Api.NetworkProxyEndpoint.None, type: Api.NetworkProxyType.None };
		emitImap({ proxy });
	};

	const handleToggleSmtpOnly = (checked: boolean) => emitImap({ imapHost: checked ? 'SMTPONLY' : '' });

	const handleCertificateChange = (
		target: 'imap' | 'exchange',
		field: 'allowAnyValidCertificate' | 'allowAnyCertificate',
		checked: boolean
	) => {
		if (target === 'imap') {
			emitImap({ [field]: checked });
		} else {
			emitExchange({ [field]: checked });
		}
	};

	const handleApplyAutoConfig = (autoConfig: Api.IEmailProviderConfiguration) => {
		if (autoConfig?.domain) {
			emit({ ...autoConfig });
		}
	};

	const renderCertificates = (options: Api.IEmailCertificateOptions | undefined, target: 'imap' | 'exchange') => (
		<div className={css(s.section)}>
			<div className={css(s.header)}>Certificates</div>
			<div className={css(s.checkboxRow)} title='Should only be used if issue is reported.'>
				<Checkbox
					id={`allowAnyValidCertificate-${target}-${configuration.key}`}
					checked={!!options?.allowAnyValidCertificate}
					onCheckedChange={checked => handleCertificateChange(target, 'allowAnyValidCertificate', checked === true)}
				/>
				<label htmlFor={`allowAnyValidCertificate-${target}-${configuration.key}`} className={css(s.checkboxLabel)}>
					Allow any valid certificate
				</label>
			</div>
			<div className={css(s.checkboxRow)} title='Should only be used if issue is reported.'>
				<Checkbox
					id={`allowAnyCertificate-${target}-${configuration.key}`}
					checked={!!options?.allowAnyCertificate}
					onCheckedChange={checked => handleCertificateChange(target, 'allowAnyCertificate', checked === true)}
				/>
				<label htmlFor={`allowAnyCertificate-${target}-${configuration.key}`} className={css(s.checkboxLabel)}>
					Allow any certificate
				</label>
			</div>
		</div>
	);

	const renderExchange = () => (
		<div className={css(s.section)}>
			<div title='If the customer accesses "https://mail.mydomain.com" on the web, their Exchange url will be "https://mail.mydomain.com/ews/Exchange.asmx"'>
				<div className={css(s.header)}>Exchange Details (?)</div>
				<div className={css(s.fields)}>
					<Input
						id={`exchangeWebServicesUrl-${configuration.key}`}
						onChange={handleExchangeUrlChange}
						placeholder='Service Url (ends with /ews/Exchange.asmx)'
						value={configuration.exchange?.webServicesUrl ?? ''}
						styles={[s.exchangeUrl]}
					/>
				</div>
			</div>
			{renderCertificates(configuration.exchange, 'exchange')}
		</div>
	);

	const renderImap = () => {
		const imap = configuration.imap ?? ({} as Api.IImapEmailConfiguration);
		const isSmtpOnly = imap.imapHost === 'SMTPONLY';
		const imapPort = imap.imapPort ? imap.imapPort.toString() : '';
		const smtpPort = imap.smtpPort ? imap.smtpPort.toString() : '';

		return (
			<div>
				{!isSmtpOnly && (
					<div className={css(s.section)}>
						<div className={css(s.header)}>ISP</div>
						<div className={css(s.fields)}>
							<Select value={imapISP} onValueChange={handleImapISPChange}>
								<SelectTrigger styles={s.selectTrigger}>
									<SelectValue placeholder='Select ISP' />
								</SelectTrigger>
								<SelectContent>
									{IMAP_PROVIDERS.map(provider => (
										<SelectItem key={provider.displayName} value={provider.displayName}>
											{provider.displayName}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
				)}
				<div className={css(s.section)}>
					<div className={css(s.header)}>IMAP Configuration</div>
					{isSmtpOnly ? (
						<div className={css(s.smtpOnlyWarning)}>
							IMAP is disabled, only email send will work. Email bounce and reply tracking will not be supported. Having
							the client upgrade to IMAP is the preferred solution.
						</div>
					) : (
						<div className={css(s.fields)}>
							<Input
								id={`imapHost-${configuration.key}`}
								placeholder='IMAP Host'
								styles={[s.hostName]}
								value={imap.imapHost ?? ''}
								onChange={e => emitImap({ imapHost: e.target.value })}
							/>
							<Input
								id={`imapPort-${configuration.key}`}
								placeholder='IMAP Port'
								styles={[s.portNumber]}
								value={imapPort}
								onChange={e => emitImap({ imapPort: parseIntFieldValue(e.target.value) })}
							/>
							<div className={css(s.checkboxRow)}>
								<Checkbox
									id={`imapSsl-${configuration.key}`}
									checked={!!imap.imapUseSsl}
									onCheckedChange={checked => emitImap({ imapUseSsl: checked === true })}
								/>
								<label htmlFor={`imapSsl-${configuration.key}`} className={css(s.checkboxLabel)}>
									Use SSL
								</label>
							</div>
						</div>
					)}
				</div>
				<div className={css(s.section)}>
					<div className={css(s.header)}>SMTP Configuration</div>
					<div className={css(s.fields)}>
						<Input
							id={`smtpHost-${configuration.key}`}
							autoComplete='off'
							placeholder='SMTP Host'
							styles={[s.hostName]}
							value={imap.smtpHost ?? ''}
							onChange={e => emitImap({ smtpHost: e.target.value })}
						/>
						<Input
							id={`smtpPort-${configuration.key}`}
							placeholder='SMTP Port'
							styles={[s.portNumber]}
							value={smtpPort}
							onChange={e => emitImap({ smtpPort: parseIntFieldValue(e.target.value) })}
						/>
						<div className={css(s.checkboxRow)}>
							<Checkbox
								id={`smtpSsl-${configuration.key}`}
								checked={!!imap.smtpUseSsl}
								onCheckedChange={checked => emitImap({ smtpUseSsl: checked === true })}
							/>
							<label htmlFor={`smtpSsl-${configuration.key}`} className={css(s.checkboxLabel)}>
								Use SSL
							</label>
						</div>
					</div>
					<div className={css(s.smtpOnly)}>
						<div className={css(s.checkboxRow)}>
							<Checkbox
								id={`smtpOnly-${configuration.key}`}
								checked={isSmtpOnly}
								onCheckedChange={checked => handleToggleSmtpOnly(checked === true)}
							/>
							<label htmlFor={`smtpOnly-${configuration.key}`} className={css(s.checkboxLabel)}>
								SMTP Only
							</label>
						</div>
					</div>
				</div>
				<div className={css(s.section)}>
					<div className={css(s.header)}>Network Proxy Endpoint</div>
					<div className={css(s.fields)}>
						<Select value={imap.proxy?.endpoint ?? Api.NetworkProxyEndpoint.None} onValueChange={handleProxyChange}>
							<SelectTrigger styles={s.selectTrigger}>
								<SelectValue placeholder='None' />
							</SelectTrigger>
							<SelectContent>
								{NETWORK_PROXY_OPTIONS.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				{renderCertificates(imap, 'imap')}
			</div>
		);
	};

	return (
		<div className={css(s.container)}>
			<div className={css(s.section)}>
				<div className={css(s.header)}>Domain</div>
				<div className={css(s.fields)}>
					<Input
						id={`domain-${configuration.key}`}
						onChange={handleDomainChange}
						placeholder='Domain'
						value={domain || ''}
						styles={[s.hostName]}
					/>
					<Select value={emailProvider || ''} onValueChange={handleProviderChange}>
						<SelectTrigger styles={s.selectTrigger}>
							<SelectValue placeholder='-- select provider --' />
						</SelectTrigger>
						<SelectContent>
							{EMAIL_PROVIDER_OPTIONS.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{!!onDelete && (
						<Button variant='destructive-secondary' size='sm' onClick={onDelete}>
							Delete
						</Button>
					)}
				</div>

				{!isSupportedISP(domain) && (
					<DangerMessage styles={[s.unsupportedISP]}>
						This email domain is a consumer ISP, and we cannot send group emails for this email domain. The customer
						needs to be provisioned a dedicated email domain. Please open a support ticket or GitHub to address.
					</DangerMessage>
				)}
			</div>

			<div className={css(s.section)}>
				<div className={css(s.checkboxRow)}>
					<Checkbox
						id={`send-from-lev-domain-${configuration.key}`}
						size='lg'
						checked={useLevDomain}
						onCheckedChange={checked => handleUseLevDomainChange(checked === true)}
						disabled={!isSupportedISP(domain) || !canUseLevDomain}
					/>
					<label htmlFor={`send-from-lev-domain-${configuration.key}`} className={css(s.checkboxLabel)}>
						Use Levitate Domain to send group emails
					</label>
				</div>
			</div>
			{useLevDomain && (
				<div className={css(s.section)}>
					<Select
						value={configuration.sendFrom?.domain}
						onValueChange={handleLevDomainChange}
						disabled={!canUseLevDomain}
					>
						<SelectTrigger styles={s.selectTrigger}>
							<SelectValue placeholder='Please select domain' />
						</SelectTrigger>
						<SelectContent>
							{candidateDomains.map(candidateDomain => (
								<SelectItem key={candidateDomain} value={candidateDomain}>
									{candidateDomain}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			<div className={css(s.section, s.sendOptions)}>
				<div>
					<div className={css(s.header)}>Custom Send Limits</div>
					<div className={css(s.fields)}>
						<div className={css(s.limitField)}>
							<label className={css(s.label)}>Daily</label>
							<Input
								id={`dailyLimit-${configuration.key}`}
								styles={[s.sendLimit]}
								onChange={handleDailyLimitChange}
								placeholder={`${defaultDailyLimit ?? ''}`}
								value={dailyLimit ?? ''}
							/>
						</div>
						<div className={css(s.limitField)}>
							<label className={css(s.label)}>Hourly</label>
							<Input
								id={`hourlyLimit-${configuration.key}`}
								styles={[s.sendLimit]}
								onChange={handleHourlyLimitChange}
								placeholder={`${defaultHourlyLimit ?? '0'}`}
								value={hourlyLimit ?? ''}
							/>
						</div>
						<div className={css(s.limitField)}>
							<label className={css(s.label)}>Seconds Between Sends</label>
							<Input
								id={`betweenSends-${configuration.key}`}
								styles={[s.secondsInput]}
								onChange={handleBetweenSendsChange}
								placeholder={`${defaultSecondsBetweenSends ?? '0'}`}
								value={secondsBetweenSends ?? ''}
							/>
						</div>
					</div>
				</div>
			</div>

			{emailProvider === 'Exchange' && renderExchange()}
			{emailProvider === 'Imap' && renderImap()}

			<Button variant='link' styles={[s.autoDiscoverButton]} onClick={() => setAutoDiscoverOpen(true)}>
				Auto Discover Configuration
			</Button>
			{autoDiscoverOpen && (
				<AutoDiscoverDialog
					initialDomain={domain}
					onApply={handleApplyAutoConfig}
					onClose={() => setAutoDiscoverOpen(false)}
				/>
			)}
		</div>
	);
};
